import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { buildHelpViewerPath, getHelpPlaybookSlugs } from '../../../help/help-routes.js';
import { hasFullHelpDocument, loadHelpDocument } from '../../../help/help-documents.js';
import { useHelp } from '../../../context/help-context.jsx';
import { getWikiArticleBySlug } from '../../../wiki/wiki-articles.js';
import './help-drawer.scss';

function HelpDrawer() {
  const { isOpen, currentSlug, closeHelp } = useHelp();
  const { t, i18n } = useTranslation();
  const [markdown, setMarkdown] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFallback, setIsFallback] = React.useState(false);
  const hasFullGuide = React.useMemo(
    () => hasFullHelpDocument(currentSlug),
    [currentSlug],
  );
  const relatedPlaybooks = React.useMemo(
    () => getHelpPlaybookSlugs(currentSlug)
      .map((slug) => getWikiArticleBySlug(slug))
      .filter(Boolean),
    [currentSlug],
  );

  React.useEffect(() => {
    if (!isOpen || !currentSlug) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsFallback(false);

    loadHelpDocument({ locale: i18n.language, slug: currentSlug, variant: 'short' })
      .then((result) => {
        if (cancelled) return;
        setMarkdown(result.markdown);
        setIsFallback(result.isFallback);
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, currentSlug, i18n.language]);

  React.useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        closeHelp();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeHelp]);

  return (
    <>
      <div
        className={`help-drawer-overlay${isOpen ? ' open' : ''}`}
        onClick={closeHelp}
      />
      <aside className={`help-drawer${isOpen ? ' open' : ''}`}>
        <div className="help-drawer-header">
          <h2 className="help-drawer-title">
            <i className="ti ti-help me-2"></i>
            {t('header.help')}
          </h2>
          <button
            type="button"
            className="help-drawer-close"
            onClick={closeHelp}
            aria-label={t('common.close')}
          >
            <i className="ti ti-x"></i>
          </button>
        </div>
        <div className="help-drawer-body">
          {isLoading && (
            <div className="help-drawer-loading">
              <i className="ti ti-loader-2 me-2"></i> {t('common.loading')}
            </div>
          )}
          {!isLoading && !currentSlug && (
            <p>{t('help.comingSoon')}</p>
          )}
          {!isLoading && currentSlug && !markdown && (
            <p>{t('help.comingSoon')}</p>
          )}
          {!isLoading && markdown && (
            <>
              {isFallback && (
                <div className="help-drawer-fallback-notice">
                  {t('help.fallbackNotice')}
                </div>
              )}
              <div className="help-markdown-body">
                <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
              </div>
              {hasFullGuide && (
                <div className="help-drawer-actions">
                  <a
                    href={buildHelpViewerPath(currentSlug)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary"
                    onClick={closeHelp}
                  >
                    <i className="ti ti-external-link me-1"></i>
                    {t('help.readFullGuide')}
                  </a>
                </div>
              )}
              {relatedPlaybooks.length > 0 && (
                <div className="help-drawer-actions">
                  <h3 className="help-drawer-related-title">{t('wiki.relatedPlaybooks')}</h3>
                  <div className="list-group list-group-flush">
                    {relatedPlaybooks.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/wiki/${article.slug}`}
                        className="list-group-item list-group-item-action px-0"
                        onClick={closeHelp}
                      >
                        {article.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default HelpDrawer;
